<template>
  <div class="card mt-4 border-warning">
    <div class="card-header bg-warning">
      <h5 class="mb-0">
        <i class="fas fa-user-shield"></i> Admin Actions
      </h5>
    </div>
    <div class="card-body">
      <!-- Email -->
      <div class="row align-items-center mb-3">
        <div class="col-3 font-weight-bold">Email</div>
        <div class="col-6">{{ user.email }}</div>
        <div class="col-3 text-right">
          <button class="btn btn-sm btn-outline-primary" @click="openEmailModal">
            <i class="fas fa-pen"></i> Change email
          </button>
        </div>
      </div>

      <hr />

      <!-- Social providers -->
      <h6 class="text-muted mb-3">Social providers</h6>

      <div
        v-for="provider in providers"
        :key="provider.key"
        class="row align-items-center mb-2"
      >
        <div class="col-3 font-weight-bold">{{ provider.label }}</div>
        <div class="col-6">
          <span v-if="user[provider.column]">
            <i class="fas fa-check text-success"></i>
            Linked
            <small class="text-muted ml-1">(sub: {{ user[provider.column] }})</small>
          </span>
          <span v-else class="text-muted">— Not linked</span>
        </div>
        <div class="col-3 text-right">
          <button
            v-if="user[provider.column]"
            class="btn btn-sm btn-outline-danger"
            :disabled="loading"
            @click="unlinkProvider(provider)"
          >
            <i class="fas fa-unlink"></i> Unlink {{ provider.label }}
          </button>
        </div>
      </div>
    </div>

    <!-- Change email modal -->
    <b-modal
      v-model="emailModalVisible"
      title="Change email"
      ok-title="Save"
      :ok-disabled="!newEmail || newEmail === user.email || loading"
      @ok="submitEmailChange"
      @hide="resetEmailModal"
    >
      <b-form-group label="New email" label-for="new-email-input">
        <b-form-input
          id="new-email-input"
          v-model="newEmail"
          type="email"
          placeholder="user@example.com"
          :state="emailError ? false : null"
        />
        <b-form-invalid-feedback>
          {{ emailError }}
        </b-form-invalid-feedback>
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import Vue from "vue";
import {
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback
} from "bootstrap-vue";

const PROVIDERS = [
  { key: "google", label: "Google", column: "google_id" },
  { key: "linkedin", label: "LinkedIn", column: "linkedin_id" }
];

export default {
  name: "UserAdminActions",
  components: {
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback
  },
  props: {
    user: {
      type: Object,
      required: true
    },
    apiBase: {
      type: String,
      required: true,
      validator: value => ["users", "jobboard-users"].includes(value)
    }
  },
  data() {
    return {
      providers: PROVIDERS,
      emailModalVisible: false,
      newEmail: "",
      emailError: "",
      loading: false
    };
  },
  methods: {
    openEmailModal() {
      this.newEmail = this.user.email;
      this.emailError = "";
      this.emailModalVisible = true;
    },
    resetEmailModal() {
      this.newEmail = "";
      this.emailError = "";
    },
    async submitEmailChange(bvEvent) {
      // Prevent the modal from closing if validation fails server-side
      if (bvEvent) bvEvent.preventDefault();
      if (!this.newEmail || this.newEmail === this.user.email) {
        this.emailModalVisible = false;
        return;
      }

      this.loading = true;
      this.emailError = "";
      try {
        await this.$http.put(
          `/enroll_admin/${this.apiBase}/${this.user.id}/email`,
          { email: this.newEmail }
        );
        Vue.$toast.success("Email updated");
        this.emailModalVisible = false;
        this.$emit("updated");
      } catch (error) {
        if (error.response && error.response.status === 422) {
          const errors = error.response.data;
          if (errors.email && Array.isArray(errors.email)) {
            this.emailError = errors.email[0];
          } else {
            this.emailError = "Invalid email";
          }
        } else {
          Vue.$toast.error("Failed to update email");
        }
      } finally {
        this.loading = false;
      }
    },
    async unlinkProvider(provider) {
      if (!confirm(`Unlink ${provider.label} from this user?`)) return;

      this.loading = true;
      try {
        await this.$http.delete(
          `/enroll_admin/${this.apiBase}/${this.user.id}/social-providers/${provider.key}`
        );
        Vue.$toast.success(`${provider.label} unlinked`);
        this.$emit("updated");
      } catch (error) {
        Vue.$toast.error(`Failed to unlink ${provider.label}`);
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>
